import { useQuery } from "@tanstack/react-query";
import { Award, Gift, Loader2, ShieldBan, UserPlus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function Community() {
  const q = useQuery({
    queryKey: ["community-overview"],
    queryFn: () => api.communityOverview(),
    refetchInterval: 30000,
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Users className="h-6 w-6 text-moving-600" /> Community & Vantaggi
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Stato community Playtomic, filler giornaliero, voucher e categorie benefit.
        </p>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Carico…
        </div>
      ) : q.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Overview non disponibile.
        </div>
      ) : q.data ? (
        <div className="space-y-5">
          {/* Communities */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 font-semibold text-slate-900">
              Community Playtomic → target {q.data.communities[0]?.target ?? 200}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {q.data.communities.map((c) => (
                <div key={c.name} className="rounded-lg border border-slate-100 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 font-medium text-slate-800">
                      {c.name}
                      {c.status ? (
                        <span
                          className={
                            c.status === "full"
                              ? "rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700"
                              : "rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700"
                          }
                        >
                          {c.status === "full" ? "piena" : "aperta"}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-slate-500">
                      {c.members ?? "?"}/{c.target}
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={cn(
                        "h-2 rounded-full",
                        (c.pct ?? 0) >= 100 ? "bg-emerald-500" : "bg-moving-500",
                      )}
                      style={{ width: `${Math.min(100, c.pct ?? 0)}%` }}
                    />
                  </div>
                  {c.gap != null && c.gap > 0 ? (
                    <div className="mt-1 text-[11px] text-slate-500">
                      mancano {c.gap} membri
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] text-emerald-600">target raggiunto ✓</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              icon={<UserPlus className="h-4 w-4" />}
              label="Filler oggi"
              value={`${q.data.filler.follows_today}+${q.data.filler.adds_today}`}
              sub={`limite ${q.data.filler.follow_limit}+${q.data.filler.add_limit}`}
            />
            <KpiCard
              icon={<Gift className="h-4 w-4" />}
              label="Voucher disponibili"
              value={String(q.data.vouchers.available)}
              sub={`${q.data.vouchers.dispensed} erogati`}
            />
            <KpiCard
              icon={<UserPlus className="h-4 w-4" />}
              label="Benvenuti"
              value={String(q.data.welcome.welcomed)}
              sub="phone unici"
            />
            <KpiCard
              icon={<ShieldBan className="h-4 w-4" />}
              label="Blocklist"
              value={String(q.data.blocklist_count)}
              sub="disiscritti perm."
            />
          </div>

          {/* Benefit categories */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 font-semibold text-slate-900">
              <Award className="h-4 w-4 text-court-600" /> Categorie benefit (Playtomic)
            </h2>
            <div className="flex flex-wrap gap-2">
              {q.data.benefits.map((b) => (
                <span
                  key={b.category}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs"
                >
                  <span className="font-medium text-slate-700">{b.category}</span>
                  <span className="rounded-full bg-court-500 px-1.5 text-[10px] font-semibold text-white">
                    {b.members}
                  </span>
                </span>
              ))}
              {q.data.benefits.length === 0 ? (
                <span className="text-xs text-slate-400">Nessuna categoria assegnata nel CSV</span>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-400">{sub}</div>
    </div>
  );
}
